import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Switch } from "@/components/atoms/Switch";
import { Text } from "@/components/atoms/Text";

/**
 *
 */
interface StatusConfig {
  showProgress: boolean;
  showTimestamps: boolean;
  showDurations: boolean;
  compactMode: boolean;
}

/**
 * Switch palette shared by every row.
 *
 * Raw hex rather than tokens because RN's `Switch` takes colour values, not
 * classNames — the same reason it was a literal before. Module scope because it
 * never depended on a prop.
 */
const SWITCH_COLORS = {
  trackColor: { false: "#767577", true: "#81b0ff" },
} as const;

/**
 * Thumb colour for a switch in the given position.
 * @param isOn - Whether the switch is on.
 * @returns The thumb colour.
 */
const switchThumbColor = (isOn: boolean): string =>
  isOn ? "#007AFF" : "#f4f3f4";

/**
 *
 */
interface StatusSettingsViewProps {
  visible: boolean;
  showStatusUpdates: boolean;
  statusDisplayConfig: StatusConfig;
  onClose: () => void;
  onToggleStatusUpdates: (value: boolean) => void;
  /** Toggles the progress row, bound by the Container. */
  onProgressChange: (value: boolean) => void;
  /** Toggles the timestamps row, bound by the Container. */
  onTimestampsChange: (value: boolean) => void;
  /** Toggles the durations row, bound by the Container. */
  onDurationsChange: (value: boolean) => void;
  /** Toggles compact mode, bound by the Container. */
  onCompactModeChange: (value: boolean) => void;
}

const StatusSettingsView = React.memo<StatusSettingsViewProps>(
  ({
    visible,
    showStatusUpdates,
    statusDisplayConfig,
    onClose,
    onToggleStatusUpdates,
    onProgressChange,
    onTimestampsChange,
    onDurationsChange,
    onCompactModeChange,
  }) =>
    !visible ? null : (
      <Box className="absolute inset-0 z-50 items-center justify-center bg-surface-inverse/50 p-5">
        <Box className="min-w-72 max-w-96 rounded-sm bg-surface-base p-5 shadow-hard-2">
          <Text
            variant="title-lg"
            className="pb-5 text-center text-content-primary"
          >
            Status Update Settings
          </Text>

          <Box className="flex-row items-center justify-between border-b border-outline-subtle py-3">
            <Text
              variant="title-sm"
              className="font-medium text-content-primary"
            >
              Show Status Updates
            </Text>
            <Switch
              value={showStatusUpdates}
              onValueChange={onToggleStatusUpdates}
              trackColor={SWITCH_COLORS.trackColor}
              thumbColor={switchThumbColor(showStatusUpdates)}
            />
          </Box>

          <Box className="flex-row items-center justify-between border-b border-outline-subtle py-3">
            <Text
              variant="title-sm"
              className="font-medium text-content-primary"
            >
              Show Progress Timeline
            </Text>
            <Switch
              value={statusDisplayConfig.showProgress}
              onValueChange={onProgressChange}
              trackColor={SWITCH_COLORS.trackColor}
              thumbColor={switchThumbColor(statusDisplayConfig.showProgress)}
            />
          </Box>

          <Box className="flex-row items-center justify-between border-b border-outline-subtle py-3">
            <Text
              variant="title-sm"
              className="font-medium text-content-primary"
            >
              Show Timestamps
            </Text>
            <Switch
              value={statusDisplayConfig.showTimestamps}
              onValueChange={onTimestampsChange}
              trackColor={SWITCH_COLORS.trackColor}
              thumbColor={switchThumbColor(statusDisplayConfig.showTimestamps)}
            />
          </Box>

          <Box className="flex-row items-center justify-between border-b border-outline-subtle py-3">
            <Text
              variant="title-sm"
              className="font-medium text-content-primary"
            >
              Show Durations
            </Text>
            <Switch
              value={statusDisplayConfig.showDurations}
              onValueChange={onDurationsChange}
              trackColor={SWITCH_COLORS.trackColor}
              thumbColor={switchThumbColor(statusDisplayConfig.showDurations)}
            />
          </Box>

          <Box className="flex-row items-center justify-between border-b border-outline-subtle py-3">
            <Text
              variant="title-sm"
              className="font-medium text-content-primary"
            >
              Compact Mode
            </Text>
            <Switch
              value={statusDisplayConfig.compactMode}
              onValueChange={onCompactModeChange}
              trackColor={SWITCH_COLORS.trackColor}
              thumbColor={switchThumbColor(statusDisplayConfig.compactMode)}
            />
          </Box>

          <Box className="pt-5">
            <Pressable
              className="items-center rounded-none bg-accent-primary p-3"
              onPress={onClose}
            >
              <Text variant="title" className="text-content-on-accent">
                Done
              </Text>
            </Pressable>
          </Box>
        </Box>
      </Box>
    )
);

StatusSettingsView.displayName = "StatusSettingsView";
export default memo(StatusSettingsView);
